/**
 * GTM Client Plugin
 * Injects GTM scripts on client-side only (no SSR)
 *
 * CSP Note: This uses dynamic script creation. If strict CSP is required,
 * see docs/metwide/analytics/gtm-ga4-implementation-spec.md for nonce approach.
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const gtmId = config.public.gtmId

  if (!gtmId) {
    if (import.meta.dev) {
      console.info('[GTM] No GTM_ID configured, skipping initialization')
    }
    return
  }

  // Initialize dataLayer synchronously so other plugins (e.g. analytics-pageview)
  // can queue events that GTM will replay FIFO once it loads.
  window.dataLayer = window.dataLayer || []

  // GTM noscript iframe for users with JS disabled. This branch only runs when
  // JS is enabled (the whole plugin does), so the iframe is never reached by a
  // no-JS user. It is kept for parity with the canonical GTM snippet, where
  // the noscript block is hand-written into the HTML body.
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.insertBefore(noscript, document.body.firstChild)

  // Defer the gtm.js script load until well after the LCP window so the
  // third-party tag-stack (gtm.js + GA4 gtag/js + Ads gtag/js + any
  // layout-reading tags such as Google Ads Calls-from-Website dynamic number
  // insertion) does not land inside the LCP measurement window and trigger
  // forced reflows. Push the gtm.start sentinel inside the same callback so
  // its timestamp reflects when GTM is actually about to evaluate (any custom
  // tag computing elapsed-time from gtm.start would otherwise be several
  // seconds long under the deferral). Earlier events pushed by
  // analytics-pageview still queue ahead of gtm.js and get processed FIFO
  // when the container loads.
  // Note: requestIdleCallback is intentionally NOT used. Its timeout option
  // is a MAX deadline, not a minimum delay; on PSI's throttled-mobile Chrome
  // the browser becomes idle ~1-1.5 s after initial hydration, well before
  // LCP, so rIC fires far earlier than the hardcoded delay below intends.
  // A plain setTimeout guarantees the deferral on every browser.
  // Tradeoff: users who bounce in the 7 s window after navigation will not
  // fire any GTM event (no page_view, no conversion). Acceptable on a B2B
  // service page where median session is well above 7 s.
  const inject = () => {
    if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) return
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
    document.head.appendChild(script)
  }
  setTimeout(inject, 7000)
})
